'use client';

import { useCallback, useEffect, useState } from 'react';
import Script from 'next/script';

import { envConfigs } from '@/config';

/**
 * Advertising + consent (CMP) plumbing for GetShot Passport.
 *
 * Everything here is INERT until a real AdSense publisher id is set in
 * NEXT_PUBLIC_ADSENSE_PUB_ID. With no id:
 *   - the consent banner never shows,
 *   - the AdSense script never loads,
 *   - AdSlot renders nothing.
 *
 * When an id IS configured, ads follow an opt-in consent model: the
 * AdSense script and any ad units load only after the visitor accepts.
 * This is the conservative stance required for EEA/UK/Switzerland. For a
 * fully TCF-compliant setup, connect Google's certified CMP (Funding
 * Choices) in the AdSense account — this banner gates our own script
 * loading and is compatible with that.
 *
 * Placement policy (per the audit): ads are only ever mounted on
 * content/guide pages. The editor, country selector, upload, result,
 * download, legal and directory pages must never render an AdSlot.
 */

const CONSENT_KEY = 'gsp_ad_consent';
const CONSENT_EVENT = 'gsp:ad-consent';
const CONSENT_OPEN_EVENT = 'gsp:open-consent';

export function adsConfigured(): boolean {
  return Boolean((envConfigs.adsense_pub_id || '').trim());
}

/** Re-open the consent banner so a visitor can review or withdraw their
 *  choice at any time (wired to the footer "Cookie Settings" link). */
export function openAdConsent() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

type Consent = 'granted' | 'denied' | 'unknown';

function readConsent(): Consent {
  if (typeof window === 'undefined') return 'unknown';
  const v = window.localStorage.getItem(CONSENT_KEY);
  return v === 'granted' || v === 'denied' ? v : 'unknown';
}

function writeConsent(value: Exclude<Consent, 'unknown'>) {
  window.localStorage.setItem(CONSENT_KEY, value);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

/** Subscribe to the current consent state (client-only). */
export function useAdConsent(): Consent {
  const [consent, setConsent] = useState<Consent>('unknown');
  useEffect(() => {
    setConsent(readConsent());
    const onChange = () => setConsent(readConsent());
    window.addEventListener(CONSENT_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return consent;
}

/* ------------------------------------------------------------------ */
/* Consent banner (lightweight CMP)                                    */
/* ------------------------------------------------------------------ */

export function ConsentBanner() {
  const [consent, setConsent] = useState<Consent>('unknown');
  const [mounted, setMounted] = useState(false);
  // `forced` is set when the visitor re-opens the banner from the footer
  // "Cookie Settings" link, so they can review or withdraw a prior choice.
  const [forced, setForced] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    setMounted(true);
    setConsent(readConsent());
    const onOpen = () => {
      setConsent(readConsent());
      setForced(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, []);

  const choose = useCallback((value: Exclude<Consent, 'unknown'>) => {
    writeConsent(value);
    setConsent(value);
    setForced(false);
    setShowDetail(false);
  }, []);

  if (!mounted) return null;

  const configured = adsConfigured();
  // Auto-show on first visit only when ads are configured; the footer link
  // can always force it open (e.g. to withdraw consent).
  const open = forced || (configured && consent === 'unknown');
  if (!open) return null;

  // With no advertising configured there are no non-essential cookies to
  // manage — show an informational panel instead of consent controls.
  if (!configured) {
    return (
      <div
        role="dialog"
        aria-label="Cookie settings"
        className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-3xl rounded-xl border border-border bg-card p-4 shadow-lg sm:p-5"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
            This site currently uses only essential cookies needed to run
            securely. No advertising or analytics cookies are set, so there is
            nothing to opt in or out of. Your photos are always processed on
            your device and never uploaded.{' '}
            <a href="/privacy-policy" className="font-semibold text-primary underline">
              Privacy policy
            </a>
          </p>
          <button
            type="button"
            onClick={() => setForced(false)}
            className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-3xl rounded-xl border border-border bg-card p-4 shadow-lg sm:p-5"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
            We use essential cookies to run this site. With your consent, we
            also allow advertising cookies on our guide pages to support the
            free tool. Your photos are always processed on your device and never
            shared.{' '}
            {consent !== 'unknown' && (
              <span className="font-medium text-foreground">
                Current choice: {consent === 'granted' ? 'Accepted' : 'Rejected'}.
              </span>
            )}{' '}
            <a href="/privacy-policy" className="font-semibold text-primary underline">
              Privacy policy
            </a>
          </p>
          <div className="flex shrink-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowDetail((v) => !v)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold hover:bg-muted"
              aria-expanded={showDetail}
            >
              Settings
            </button>
            <button
              type="button"
              onClick={() => choose('denied')}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={() => choose('granted')}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Accept
            </button>
          </div>
        </div>
        {showDetail && (
          <dl className="grid gap-3 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground sm:grid-cols-2">
            <div>
              <dt className="font-semibold text-foreground">
                Essential (always on)
              </dt>
              <dd>Required to deliver the site securely. Cannot be disabled.</dd>
            </div>
            <div>
              <dt className="font-semibold text-foreground">
                Advertising (optional)
              </dt>
              <dd>
                Google and its partners set cookies on our guide pages only if
                you Accept. Rejecting keeps them off; the advertising script is
                not loaded until you consent.
              </dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}

/**
 * Footer "Cookie Settings" control. Client-only button that re-opens the
 * consent banner so visitors can review or withdraw consent at any time.
 */
export function CookieSettingsButton({
  className,
}: {
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => openAdConsent()}
      className={
        className ??
        'text-muted-foreground hover:text-primary block text-xs break-words underline duration-150'
      }
    >
      Cookie Settings
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* AdSense loader — only on content pages, only after consent          */
/* ------------------------------------------------------------------ */

export function AdSenseScript() {
  const consent = useAdConsent();
  const pub = (envConfigs.adsense_pub_id || '').trim();
  if (!pub || consent !== 'granted') return null;
  return (
    <Script
      id="adsbygoogle-init"
      async
      strategy="afterInteractive"
      crossOrigin="anonymous"
      src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${pub}`}
    />
  );
}

/**
 * A single labelled ad slot. Renders nothing unless ads are configured,
 * a slot id is supplied, and the visitor has granted consent. Mount this
 * ONLY inside article/guide content — never near the editor or controls.
 */
export function AdSlot({
  slot,
  className,
}: {
  slot?: string;
  className?: string;
}) {
  const consent = useAdConsent();
  const pub = (envConfigs.adsense_pub_id || '').trim();
  const slotId = (slot || envConfigs.adsense_slot_article || '').trim();

  useEffect(() => {
    if (!pub || !slotId || consent !== 'granted') return;
    try {
      const w = window as unknown as { adsbygoogle?: Record<string, unknown>[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
    } catch {
      /* AdSense not ready yet — it retries on its own */
    }
  }, [pub, slotId, consent]);

  if (!pub || !slotId || consent !== 'granted') return null;

  return (
    <div className={className}>
      <div className="mb-1 text-center text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
        Advertisement
      </div>
      <ins
        className="adsbygoogle block"
        style={{ display: 'block' }}
        data-ad-client={pub}
        data-ad-slot={slotId}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
